import { useEffect, useMemo, useState } from 'react';
import { Avatar, Dropdown, Layout, Menu, Space, Tag } from '@arco-design/web-react';
import {
  IconDashboard,
  IconIdcard,
  IconNotification,
  IconExclamationCircle,
  IconBug,
  IconExperiment,
  IconSync,
  IconTool,
  IconPoweroff,
  IconUserGroup,
  IconUser,
  IconSafe,
  IconBook,
} from '@arco-design/web-react/icon';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuthStore } from '@/store/authStore';

const { Sider, Header, Content } = Layout;

type MenuLeaf = { key: string; label: string; icon: JSX.Element };
type MenuGroup = { key: string; label: string; icon: JSX.Element; children: MenuLeaf[] };
type MenuEntry = MenuLeaf | MenuGroup;

const isMenuGroup = (entry: MenuEntry): entry is MenuGroup => 'children' in entry;

const menuConfig: MenuEntry[] = [
  { key: '/dashboard', label: '工作台', icon: <IconDashboard /> },
  { key: '/customers', label: '客户中心', icon: <IconUserGroup /> },
  { key: '/cards', label: '卡片管理', icon: <IconIdcard /> },
  {
    key: 'funds',
    label: '资金管理',
    icon: <IconSafe />,
    children: [
      { key: '/wallet-ledger', label: '资金流水', icon: <IconBook /> },
      { key: '/reconcile', label: '与上游对账', icon: <IconSync /> },
    ],
  },
  {
    key: 'debug',
    label: '调试功能',
    icon: <IconBug />,
    children: [
      { key: '/webhooks', label: 'Webhook 收件箱', icon: <IconNotification /> },
      { key: '/upstream-errors', label: '上游错误日志', icon: <IconExclamationCircle /> },
      { key: '/upstream-comm', label: '上游通讯调试', icon: <IconBug /> },
      { key: '/upstream-check', label: '检查上游', icon: <IconExclamationCircle /> },
      { key: '/upstream-lab', label: '上游联调台', icon: <IconTool /> },
      { key: '/mock/simulate', label: 'Mock 模拟交易', icon: <IconExperiment /> },
    ],
  },
];

const menuLeaves = menuConfig.flatMap((entry) => (isMenuGroup(entry) ? entry.children : [entry]));

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, logout } = useAdminAuthStore();
  const [openKeys, setOpenKeys] = useState(['debug', 'funds']);

  const selectedKey = useMemo(() => {
    const match = menuLeaves
      .filter((item) => location.pathname.startsWith(item.key))
      .sort((a, b) => b.key.length - a.key.length)[0];
    return match?.key ?? '/dashboard';
  }, [location.pathname]);

  useEffect(() => {
    const parentKeys = menuConfig
      .filter(
        (entry) =>
          isMenuGroup(entry) && entry.children.some((child) => selectedKey.startsWith(child.key)),
      )
      .map((entry) => entry.key);
    if (parentKeys.length === 0) return;
    setOpenKeys((prev) => Array.from(new Set([...prev, ...parentKeys])));
  }, [selectedKey]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={220} style={{ background: 'var(--admin-brand)' }}>
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            padding: '0 20px',
            color: '#fff',
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: 1,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          UP·CARD ADMIN
        </div>
        <Menu
          theme="dark"
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onClickSubMenu={(_, openKeys) => setOpenKeys(openKeys)}
          onClickMenuItem={(key) => navigate(key)}
          style={{ background: 'transparent' }}
        >
          {menuConfig.map((entry) =>
            isMenuGroup(entry) ? (
              <Menu.SubMenu
                key={entry.key}
                title={
                  <Space>
                    {entry.icon}
                    {entry.label}
                  </Space>
                }
              >
                {entry.children.map((child) => (
                  <Menu.Item key={child.key}>
                    <Space>
                      {child.icon}
                      {child.label}
                    </Space>
                  </Menu.Item>
                ))}
              </Menu.SubMenu>
            ) : (
              <Menu.Item key={entry.key}>
                <Space>
                  {entry.icon}
                  {entry.label}
                </Space>
              </Menu.Item>
            ),
          )}
        </Menu>
      </Sider>

      <Layout>
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#fff',
            height: 56,
            padding: '0 24px',
            borderBottom: '1px solid var(--admin-border-light)',
          }}
        >
          <Space size="large">
            <Tag color="green" bordered>
              管理端 · {import.meta.env.MODE}
            </Tag>
            <span style={{ color: 'var(--admin-text-secondary)' }}>
              所有写操作均会写入审计日志
            </span>
          </Space>
          <Dropdown
            droplist={
              <Menu
                onClickMenuItem={async (key) => {
                  if (key === 'logout') {
                    await logout();
                    navigate('/login');
                  }
                }}
              >
                <Menu.Item key="logout">
                  <IconPoweroff /> 退出登录
                </Menu.Item>
              </Menu>
            }
          >
            <Space style={{ cursor: 'pointer' }}>
              <Avatar size={32} style={{ backgroundColor: 'var(--admin-brand-2)' }}>
                <IconUser />
              </Avatar>
              <span>{profile?.username ?? 'admin'}</span>
            </Space>
          </Dropdown>
        </Header>
        <Content style={{ background: 'var(--admin-bg-page)' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
